import React, { useCallback, useEffect, useState } from "react";
import { FilterList, Search } from "@mui/icons-material";
import { SearchBar, SearchBarProps } from "./SearchBar";
import { useRef } from "react";
import Fuse, { FuseResult } from "fuse.js";
import clsx from "clsx";

export type FuzzyFilterData = {
	id: string;
	text: string;
	data?: any;
};

export type FuzzyFilterResults = FuseResult<FuzzyFilterData>[] | undefined;

type FuzzyFilterBarProps = {
	data: FuzzyFilterData[];
	onFilter: (params: { results: FuzzyFilterResults, value: string}) => void;
	placeholder?: string;
	icon?: React.ReactNode;
	limit?: number;
	value?: string;
	keys?: string[];
	shortcut?: string;
};

export function FuzzyFilterBar({
	data,
	onFilter,
	placeholder = 'Filter',
	icon,
	limit,
	value: initialValue,
	keys = ['text'],
	className,
	...rest
}: FuzzyFilterBarProps & SearchBarProps & React.HTMLAttributes<HTMLDivElement>) {
	const [value, setValue] = useState<string>(initialValue ?? '');

	const fuse = useRef<Fuse<FuzzyFilterData> | null>(null);
	useEffect(() => {
		if (data) {
			fuse.current = new Fuse(data, {
				keys,
				includeScore: true,
				includeMatches: true,
				ignoreLocation: true,
				useExtendedSearch: true,
				minMatchCharLength: 1,
			});
			search(value);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const search = useCallback((value: string) => {
		if (value) {
			const results = fuse.current?.search(value, { limit });
			const sorted = results?.sort((a, b) => a.score! - b.score!);
			onFilter({ results: sorted, value });
		} else {
			onFilter({ results: undefined, value });
		}
	}, [fuse, limit, onFilter]);

	const handleChange = useCallback((v: string) => {
		setValue(v);
		search(v);
	}, [search]);

	useEffect(() => {
		handleChange(initialValue ?? '');
	}, [initialValue, handleChange]);

	return (
		<SearchBar
			className={clsx(
				'gap-[15px]! px-[20px]! h-[40px] rounded-md',
				' [&_.closeIcon]:size-[18px]!',
				className,
			)}
			icon={icon ?? <Search className="text-fg-primary size-[18px]!" />}
			placeholder={placeholder}
			onChange={handleChange}
			value={value}
			{...rest}
		/>
	);
}